import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Bell,
  Activity,
  FileText,
  ArrowLeftRight,
  Brain,
  UserCheck,
  Users,
  BarChart3,
  Settings,
} from "lucide-react";
import type { SidebarNavItem } from "./DesktopLayout.types";

const navItems: Omit<SidebarNavItem, "isActive">[] = [
  { id: "alerts", label: "Alerts", icon: <Bell className="size-6" />, path: "/alerts" },
  { id: "news", label: "News", icon: <FileText className="size-6" />, path: "/alerts/stocks" },
  {
    id: "price-alerts",
    label: "Price Alerts",
    icon: <Activity className="size-6" />,
    path: "/price-alerts",
  },
  { id: "trade", label: "Trade", icon: <ArrowLeftRight className="size-6" />, path: "/positions" },
  { id: "analyze", label: "Analyze", icon: <Brain className="size-6" />, path: "/analyze" },
  {
    id: "copy-trading",
    label: "Copy Trading",
    icon: <UserCheck className="size-6" />,
    path: "/copy-trading",
  },
  { id: "referrals", label: "Referrals", icon: <Users className="size-6" />, path: "/referrals" },
  {
    id: "statistics",
    label: "Statistics",
    icon: <BarChart3 className="size-6" />,
    path: "/statistics",
  },
  { id: "settings", label: "Settings", icon: <Settings className="size-6" />, path: "/settings" },
];

interface SidebarNavProps {
  /** Optional additional content to render at the bottom of the sidebar */
  bottomContent?: React.ReactNode;
}

/** Glass navigation column from the redesign. */
export const SidebarNav: React.FC<SidebarNavProps> = ({ bottomContent }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const paths = navItems.map((item) => item.path);

  // The longest matching path wins, so /alerts/stocks highlights News, not Alerts.
  const activePath = paths
    .filter((path) => location.pathname === path || location.pathname.startsWith(`${path}/`))
    .sort((a, b) => b.length - a.length)[0];

  return (
    <aside className="glass flex h-full w-60 shrink-0 flex-col overflow-hidden !rounded-3xl">
      <button className="flex items-center gap-1 px-4 py-6" onClick={() => navigate("/")}>
        <img src="/logo-mark.svg" alt="" width={38} height={32} />
        <img src="/logo-wordmark.svg" alt="Trigger" width={84} height={24} />
      </button>

      <nav className="flex flex-1 flex-col gap-2 overflow-y-auto px-4 py-3">
        {navItems.map((item) => {
          const active = item.path === activePath;
          return (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              className={`relative flex w-full items-center gap-2 overflow-hidden rounded-xl p-3 text-left text-sm leading-5 transition-colors ${
                active
                  ? "border-l-[3px] border-[#2dcc89] bg-[rgba(7,166,108,0.1)] text-[#e5e5e5]"
                  : "text-[#d4d4d4] hover:bg-white/5"
              }`}
            >
              {active && (
                <span
                  aria-hidden
                  className="pointer-events-none absolute -left-1 inset-y-0 w-3 bg-[radial-gradient(ellipse_at_left,rgba(45,204,137,0.55),transparent_70%)]"
                />
              )}
              <span className={active ? "text-[#e5e5e5]" : "text-[#a3a3a3]"}>{item.icon}</span>
              <span className={active ? "font-semibold" : "font-medium"}>{item.label}</span>
            </button>
          );
        })}
      </nav>

      {bottomContent && <div className="border-t border-white/10 p-4">{bottomContent}</div>}
    </aside>
  );
};
